'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Autoplay, Pagination } from 'swiper/modules';

import Carousel from '@commons/components/Carousel';
import Icon from '@commons/components/Icon';
import BaseContainer from '@commons/containers/BaseContainer';
import ResponsiveContainer from '@commons/containers/ResponsiveContainer';
import type { SiteAlert as SiteAlertProps } from '@commons/services/alert';

import SiteAlert from './Alert';
import styles from './Banner.module.scss';

// Type definition of the AlertsBanner component properties.

type Props = {
  alerts: SiteAlertProps[];
};

/**
 * Alerts banner component.
 */

const AlertsBanner = ({ alerts }: Props) => {
  const t = useTranslations('commons');
  const [visible, setVisible] = useState(true);

  const alertVersion = alerts.map((a) => a.id).join('_');
  const alertsKey = 'siteAlertDismissed';

  useEffect(() => {
    const storedAlerts = localStorage.getItem(alertsKey);
    setVisible(storedAlerts !== alertVersion);
  }, [alertVersion]);

  const handleClose = () => {
    localStorage.setItem(alertsKey, alertVersion);
    setVisible(false);
  };

  // Early return if there are no site alerts or hidden.
  if (!visible || alerts.length === 0) return null;

  const hasMultipleAlerts = alerts.length > 1;

  const renderedAlerts = alerts.map((alert) => (
    <Carousel.Slide className={styles.slide} key={alert.id}>
      <SiteAlert {...alert} />
    </Carousel.Slide>
  ));

  const renderedCloseButton = (
    <button onClick={handleClose} aria-label={t('close')} className={styles.closeBtn}>
      <Icon type="close" />
    </button>
  );

  return (
    <ResponsiveContainer
      theme="black"
      paddingTop={hasMultipleAlerts ? 'lg' : 'md'}
      paddingBottom="md"
      horizontalAlignment="center"
      className={styles.banner}
    >
      <BaseContainer width={{ xs: 12, lg: 10 }}>
        <Carousel
          loop
          speed={1000}
          modules={[Autoplay, Pagination]}
          autoplay={{ delay: 7500, pauseOnMouseEnter: true }}
          slidesPerView={1}
          pagination={{ clickable: true }}
          className={clsx(styles.swiper, hasMultipleAlerts && styles.enabled)}
        >
          {renderedAlerts}
        </Carousel>
        {renderedCloseButton}
      </BaseContainer>
    </ResponsiveContainer>
  );
};

export default AlertsBanner;
